/**
 * Gradient ramps for the WebGL canvas (webgl.ts), shared between frames.
 *
 * Each pixi `FillGradient` paints its own little canvas the first time it is used. The world picture asks for new
 * gradients every frame (a warp circle's light column moves with the camera and pulses), so that was ~1000 canvases
 * a second for Blink's GC to trace (docs/review/perf/2026-10-13-hitch-trace.md). The ramp texture depends only on
 * the stops (pixi quantises them to 8-bit RGBA) and, for a radial gradient, its shape in the 64-px square; where it
 * sits is only the transform. So the textures are kept here, keyed by what changes their pixels, and every gradient
 * gets a fresh, light `FillGradient` (no canvas) carrying the shared texture and its own transform, worked out as
 * pixi does (FillGradient.buildLinearGradient / buildRadialGradient, textureSpace 'global').
 */
import { FillGradient, Matrix, type Texture } from 'pixi.js';

const SIZE = 64;
/** Ramps kept; the least recently used go when over, but never one used this frame. */
const CAP = 128;

export class GradientTextures {
  private readonly ramps = new Map<string, { texture: Texture; used: number }>();
  /** Gradients that could not share (a degenerate radius): destroyed at the next frame's start. */
  private loose: FillGradient[] = [];
  private frame = 0;

  /** A new frame: last frame's unshared gradients are released. */
  begin(frame: number): void {
    this.frame = frame;
    for (const f of this.loose) f.destroy();
    this.loose = [];
  }

  /** A gradient fill for `args` (linear x0,y0,x1,y1 or radial x0,y0,r0,x1,y1,r1) and its stops. */
  fill(args: readonly number[], stops: readonly (readonly [number, string])[]): FillGradient {
    const radial = args.length === 6;
    const fill = radial
      ? new FillGradient({
          type: 'radial',
          center: { x: args[0]!, y: args[1]! },
          innerRadius: args[2]!,
          outerCenter: { x: args[3]!, y: args[4]! },
          outerRadius: args[5]!,
          textureSpace: 'global',
          textureSize: SIZE,
        })
      : new FillGradient({
          start: { x: args[0]!, y: args[1]! },
          end: { x: args[2]!, y: args[3]! },
          textureSpace: 'global',
          textureSize: SIZE,
        });
    for (const [offset, color] of stops) fill.addColorStop(offset, color);
    const placed = radial ? radialPlace(args) : linearPlace(args);
    if (!placed) {
      this.loose.push(fill);
      return fill;
    }
    let key = placed.key;
    for (const s of fill.colorStops) key += `|${s.offset},${s.color as string}`;
    const ramp = this.ramps.get(key);
    if (ramp) {
      // Most recent last: the map's order is the eviction order.
      this.ramps.delete(key);
      this.ramps.set(key, ramp);
      ramp.used = this.frame;
      fill.texture = ramp.texture;
      fill.transform = placed.transform;
      return fill;
    }
    fill.buildGradient();
    this.ramps.set(key, { texture: fill.texture, used: this.frame });
    this.evict();
    return fill;
  }

  private evict(): void {
    if (this.ramps.size <= CAP) return;
    for (const [key, ramp] of this.ramps) {
      if (this.ramps.size <= CAP) break;
      if (ramp.used >= this.frame) continue;
      ramp.texture.destroy(true);
      this.ramps.delete(key);
    }
  }

  destroy(): void {
    for (const ramp of this.ramps.values()) ramp.texture.destroy(true);
    this.ramps.clear();
    for (const f of this.loose) f.destroy();
    this.loose = [];
  }
}

/** pixi's linear placement (clamp-to-edge, global space) and what decides its ramp's pixels (the flip). */
function linearPlace(a: readonly number[]): { key: string; transform: Matrix } | null {
  let x0 = a[0]!,
    y0 = a[1]!,
    x1 = a[2]!,
    y1 = a[3]!;
  let dx = x1 - x0;
  let dy = y1 - y0;
  const flip = dx < 0 || dy < 0;
  if (dx < 0) {
    [x0, x1] = [x1, x0];
    dx *= -1;
  }
  if (dy < 0) {
    [y0, y1] = [y1, y0];
    dy *= -1;
  }
  const dist = Math.sqrt(dx * dx + dy * dy);
  if (!Number.isFinite(dist)) return null;
  const m = new Matrix();
  m.scale(dist / SIZE, 1);
  m.rotate(Math.atan2(dy, dx));
  m.translate(x0, y0);
  return { key: flip ? 'L1' : 'L0', transform: m };
}

/** pixi's radial placement (global space) and what decides its ramp's pixels (the circles within the square). */
function radialPlace(a: readonly number[]): { key: string; transform: Matrix } | null {
  const [x0, y0, r0, x1, y1, r1] = a as [number, number, number, number, number, number];
  const ox = x1 - r1;
  const oy = y1 - r1;
  const scale = SIZE / (r1 * 2);
  if (!Number.isFinite(scale) || scale <= 0) return null;
  const cx = (x0 - ox) * scale;
  const cy = (y0 - oy) * scale;
  const m = new Matrix();
  m.scale(1 / scale, 1 / scale);
  m.translate(ox, oy);
  // Rounded to 1/1024 px of the 64-px ramp: float noise (x0 - (x1 - r1)) must not split one ramp into many.
  const q = (v: number) => Math.round(v * 1024) / 1024;
  return { key: `R${q(cx)},${q(cy)},${q(r0 * scale)},${q((x1 - ox) * scale)},${q((y1 - oy) * scale)}`, transform: m };
}
